import { useTranslation } from '@front10/utils';
import LocationSelector from '../LocationSelector';
import Box from '../../elements/Box';
import Row from '../../elements/Row';
import Col from '../../elements/Col';
import Text from '../../elements/Text';
import Button from '../../elements/Button';
import Checkbox from './CheckboxItem';
import { Context } from '@front10/helpers/dist/Context';
import { MobileFooter } from '../../lab/MobileFilterView';
import React, { useContext, useState, useEffect } from 'react';
import { ThemeProvider } from '@front10/base-elements';
import { EmotionCache } from '../../containers/_commons/emotionCache';
import VisuallyHidden from '@reach/visually-hidden';

interface ImperativeActions {} //Momentarily not necessary

function filterContainsPrepopulatedOptions(options: any) {
  return Array.isArray(options) && options.length > 0 && options[0]?.label === undefined;
}
function updatePrepopulatedOptions({
  selectedItemsState,
  data,
  travelInterestId,
  itemAllEelements
}: any) {
  let selectedOptions = [];
  selectedOptions = data[
    travelInterestId
  ].options.filter((option: { label: string; value: string; weight: number }) =>
    selectedItemsState.some((opt: { value: string }) => opt.value === option.value)
  );
  if (
    data[travelInterestId].options.length === selectedItemsState.length ||
    selectedItemsState.find((obj: any) => obj.value === 'any')
  ) {
    selectedOptions.unshift(itemAllEelements);
  }
  return selectedOptions;
}

interface ItemProps {
  label: string;
  weight: number;
  value: string;
}

function TravelerInterest(
  {
    toggleElement,
    travelInterestId,
    listItem,
    allItems,
    mapperIcons,
    isMobile = false,
    footerContainer = {},
    buttonsContainer = {},
    defaultIcon,
    travelersInterest: { data = {} },
    onChange,
    onClick,
    onClear,
    selectedItems: selectedItemsState,
    enableSearchQuantityItems = 10,
    clearButtonSettings,
    doneButtonSettings,
    separatorStyle = {
      'cmp-styles': {
        'border-top': '1px solid rgba(0,0,0,.125)'
      }
    },
    isDirty,
    setWasClear,
    showToggleButtonLabels = true,
    selectedStyle = {
      'cmp-styles': {
        'font-weight': '300',
        'padding-left': '3px'
      }
    },
    countSelectedStyle = {
      'cmp-styles': {
        'font-weight': 'bold!important'
      }
    },
    focusLock = true,
    useAsDialog = true,
    dialogAriaLabel,
    ...props
  }: $TSFixMe,
  ref: React.Ref<ImperativeActions>
) {
  const translate = useTranslation();
  const {
    direction,
    theme: { designTokens }
  } = useContext(Context);
  const contextTranslate = useContext(Context).translate;
  const { alwaysEnabledDoneButton = true } = doneButtonSettings;

  const ITEM_WEIGHT_ELEMENTS = -1;
  const ITEM_ANY_ELEMENTS = 'any';
  const ENTER_KEY = 13;
  const SPACE_KEY = 32;
  const TAB_KEY = 9;
  const ESC_KEY = 27;
  const ARROW_UP_KEY = 38;
  const ARROW_DOWN_KEY = 40;

  const ITEM_ALL_ELEMENTS = React.useMemo(() => {
    return {
      label: contextTranslate('cmp-traveler-interest-any', 'Any interests'),
      weight: ITEM_WEIGHT_ELEMENTS,
      value: ITEM_ANY_ELEMENTS
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ITEM_WEIGHT_ELEMENTS, translate]);

  const options = React.useMemo(() => {
    if (data && data[travelInterestId]) {
      const opts = data[travelInterestId].options;
      return [ITEM_ALL_ELEMENTS, ...opts];
    }
    return [];
  }, [ITEM_ALL_ELEMENTS, data, travelInterestId]);

  const uppdateToggleButtonLabel = (selectedItems: any) => {
    let newlabel: string;
    let tmp = [];
    for (let index = 0; index < selectedItems.length; index++) {
      const element = selectedItems[index];
      tmp.push(element.label);
    }
    const opts = data[travelInterestId]?.options ? data[travelInterestId].options : [];

    if (selectedItems.length > 0 && selectedItems.length < opts.length + 1) {
      newlabel = tmp
        .sort()
        .join(props.inputItem.labelSeparator ? props.inputItem.labelSeparator : ', ');
      // newlabel = contextTranslate('cmp-traveler-interests-title', 'Select interest').concat(
      //   ' ',
      //   newlabel
      // );
    } else if (selectedItems.length === opts.length + 1) {
      newlabel = contextTranslate('cmp-filter-all-items-selected', 'All items selected');
    } else {
      newlabel = contextTranslate('cmp-traveler-interest-filter-placeholder', 'Traveler Interest');
    }

    setLabelSelectedItems({ label: newlabel, value: newlabel });
  };

  React.useEffect(() => {
    if (!selectedItemsState[travelInterestId]) {
      setSelectedItems([]);
      uppdateToggleButtonLabel([]);
    } else {
      const newSelectedItemsState = selectedItemsState[travelInterestId].selectedItems;
      if (filterContainsPrepopulatedOptions(newSelectedItemsState)) {
        if (data && data[travelInterestId]) {
          const selectedOptions = updatePrepopulatedOptions({
            selectedItemsState: newSelectedItemsState,
            data,
            travelInterestId,
            itemAllEelements: ITEM_ALL_ELEMENTS
          });
          setSelectedItems(selectedOptions);
          uppdateToggleButtonLabel(selectedOptions);
          setCopySelectedItems(newSelectedItemsState);
        }
      } else {
        setSelectedItems(newSelectedItemsState);
        uppdateToggleButtonLabel(newSelectedItemsState);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItemsState]);

  const [activateDoneButton, setActivateDoneButton] = useState(false);
  const [count, setCount] = useState(0);
  const [itemSelectedByKeyboard, setItemSelectedByKeyboard] = useState<any>(null);
  const [isOpen, setIsOpen] = useState(false);
  const locationSelectorInputRef = React.useRef<any>(null);
  const locationSelectorRef = React.useRef<any>();
  const doneButtonRef = React.useRef<any>();
  const secondarySearchFilterRef = React.useRef<any>();
  const [selectedItems, setSelectedItems] = React.useState<[ItemProps] | any[]>(() => {
    if (Boolean(selectedItemsState[travelInterestId])) {
      return selectedItemsState[travelInterestId].selectedItems;
    } else {
      return [];
    }
  });
  const [copySelectedItems, setCopySelectedItems] = useState(() => {
    if (Boolean(selectedItemsState[travelInterestId])) {
      return selectedItemsState[travelInterestId].selectedItems;
    } else {
      return [];
    }
  });
  const [clear, setClear] = useState(true);
  const [navigation, setNavigation] = useState(false);
  const labelToggleButton = contextTranslate(
    'cmp-traveler-interest-filter-placeholder',
    'Traveler Interest'
  );

  const [labelSelectedItems, setLabelSelectedItems] = useState({
    label: labelToggleButton,
    value: labelToggleButton
  });

  useEffect(() => {
    if (copySelectedItems.length === 0) {
      setLabelSelectedItems({
        label: labelToggleButton,
        value: labelToggleButton
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItems]);

  React.useEffect(() => {
    const newSelectedItemsState = selectedItemsState[travelInterestId]?.selectedItems;
    if (filterContainsPrepopulatedOptions(newSelectedItemsState)) {
      if (data && data[travelInterestId]) {
        const selectedOptions = updatePrepopulatedOptions({
          selectedItemsState: newSelectedItemsState,
          data,
          travelInterestId,
          itemAllEelements: ITEM_ALL_ELEMENTS
        });
        setSelectedItems(selectedOptions);
        uppdateToggleButtonLabel(selectedOptions);
        setCopySelectedItems(selectedOptions);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    let cSelectedItems = [];
    if (Boolean(selectedItemsState[travelInterestId])) {
      cSelectedItems = selectedItemsState[travelInterestId].selectedItems;
      if (filterContainsPrepopulatedOptions(cSelectedItems)) {
        if (data && data[travelInterestId]) {
          const selectedOptions = updatePrepopulatedOptions({
            selectedItemsState: cSelectedItems,
            data,
            travelInterestId,
            itemAllEelements: ITEM_ALL_ELEMENTS
          });
          cSelectedItems = [...selectedOptions];
        }
      }
    }
    if (selectedItemsState[travelInterestId] && isOpen && clear) {
      setSelectedItems(cSelectedItems);
      setCopySelectedItems(cSelectedItems);
    }
    if (isOpen && !selectedItemsState[travelInterestId]) {
      setCopySelectedItems([]);
      setSelectedItems([]);
      setActivateDoneButton(false);
    }
    setClear(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (props.navigation) {
      setNavigation(true);
      locationSelectorRef.current?.onOpenMenu();
    } else {
      setNavigation(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.navigation]);

  function renderItem(item: ItemProps, locationProps: $TSFixMe) {
    const focusetItemValue =
      itemSelectedByKeyboard !== null && item.label === itemSelectedByKeyboard?.label;
    if (focusetItemValue) {
      setItemSelectedByKeyboard(null);
    }
    return (
      <>
        <Checkbox
          {...props}
          {...listItem}
          // {...locationProps}
          icon={mapperIcons[item.value] ? mapperIcons[item.value] : defaultIcon}
          allItems={item.weight === ITEM_WEIGHT_ELEMENTS ? allItems : {}}
          isMobile={isMobile}
          item={item}
          isChecked={selectedItems.find((x: ItemProps) => x.value === item.value)}
          showSideMessage={false}
          onChange={(_: boolean, itemMarked: any) => {
            setCount(-1);
            if (isMobile) {
              // const newSelectedItems: any = selectItem(itemMarked);
              // setSelectedItems(newSelectedItems);
              if (secondarySearchFilterRef?.current?.getValue() !== '') {
                secondarySearchFilterRef?.current?.clear();
                setItemSelectedByKeyboard(item);
              }
            }
            setActivateDoneButton(true);
            setTimeout(() => {
              setCount(
                selectedItems.find((x: ItemProps) => x.value === itemMarked.value) === undefined
                  ? selectedItems.length + 1
                  : selectedItems.length - 1
              );
            }, 1100);
          }}
          onKeyDown={(e: any) => {
            if (e.keyCode === ENTER_KEY || e.keyCode === SPACE_KEY) {
              e.preventDefault();
              if (selectedItems.find((x: ItemProps) => x.value === item.value) !== undefined) {
                if (item.value === ITEM_ANY_ELEMENTS) {
                  setSelectedItems([]);
                } else {
                  let newValues = [];
                  for (let index = 0; index < selectedItems.length; index++) {
                    const element = selectedItems[index];
                    if (element.value !== item.value && element.value !== ITEM_ANY_ELEMENTS) {
                      newValues.push(element);
                    }
                  }
                  setSelectedItems(newValues);
                }
              } else {
                let newValues = [...selectedItems, item];
                const opts = data[travelInterestId].options;
                if (
                  item.value === ITEM_ANY_ELEMENTS ||
                  (item.value !== ITEM_ANY_ELEMENTS && opts.length === newValues.length)
                ) {
                  newValues = [ITEM_ALL_ELEMENTS, ...opts];
                }
                setSelectedItems(newValues);
              }
              setActivateDoneButton(true);
              //INFO: clearing the secondary search filter after selecting an item in case it has value
              if (secondarySearchFilterRef?.current?.getValue() !== '') {
                secondarySearchFilterRef?.current?.clear();
                setItemSelectedByKeyboard(item);
              }
            }
            if (!e.shiftKey && e.key === 'Tab' && !alwaysEnabledDoneButton) {
              const opts = data[travelInterestId].options;
              if (
                item.value === opts[opts.length - 1].value &&
                selectedItems.length === 0 &&
                !activateDoneButton
              ) {
                e.preventDefault();
                locationSelectorRef.current?.onCloseMenu();
                focusFilterButton();
                if (setWasClear) {
                  setWasClear(true);
                }
              }
            }
          }}
          enableFocus
          setFocusOnItem={focusetItemValue}
          onKeyUp={onKeyUp}
        />
        {item.weight < 0 && <Box {...separatorStyle} />}
      </>
    );
  }

  const enableSecondarySearch = React.useMemo(
    () => options.length - 1 >= enableSearchQuantityItems,
    [enableSearchQuantityItems, options.length]
  );

  const handleClear = () => {
    setSelectedItems([]);
    // if (secondarySearchFilterRef.current) secondarySearchFilterRef.current?.focus();
    // else focusFilterButton();
    doneButtonRef.current?.focus();

    // let payload: any = {};
    // payload[data[travelInterestId].id] = undefined;
    // onClear(data[travelInterestId].id);
    setActivateDoneButton(true);
    if (isMobile) {
      locationSelectorRef.current?.onOpenMenu();
      setClear(false);
    }
  };

  const handleSave = () => {
    let payload: any = {};
    const value: any = Object.assign({}, { ...data[travelInterestId], selectedItems });
    delete value.id;
    delete value.options;
    payload[data[travelInterestId].id] = value;
    focusFilterButton();
    locationSelectorRef.current?.onCloseMenu(); //Churre
    if (payload[data[travelInterestId].id].selectedItems.length === 0) {
      onClear(data[travelInterestId].id);
    } else {
      onChange(payload);
    }

    setActivateDoneButton(false);
    uppdateToggleButtonLabel(selectedItems);
    if (props.navigation) {
      props.goBack();
      locationSelectorRef.current?.onCloseMenu();
    }
    if (setWasClear) {
      setWasClear(true);
    }
    locationSelectorInputRef.current?.setAttribute('data-focus', 'no-focus');
  };

  const focusFilterButton = () => {
    if (locationSelectorRef) {
      locationSelectorRef?.current?.focusSelectorInput();
    }
  };
  useEffect(() => {
    if (selectedItems.length === 0) {
      new Promise((resolve) => setTimeout(resolve, 700)).then(() => setCount(0));
    }
  }, [selectedItems.length]);

  const renderFooter = () => (
    <Box display="flex" {...footerContainer}>
      <div aria-live="polite" aria-atomic="true">
        {selectedItems.length !== 0 && (
          <>
            <Text dataTestId="traveler-interest-filter-counter" {...countSelectedStyle}>
              {selectedItems[0].value === 'any' ? selectedItems.length - 1 : selectedItems.length}
            </Text>
            <Text {...selectedStyle}>
              {translate('cmp-traveler-info-options-selected', ' selected')}
            </Text>
          </>
        )}
      </div>
      <Row {...buttonsContainer}>
        <Col>
          <Button
            color="light"
            {...clearButtonSettings}
            disabled={selectedItems.length === 0}
            onMouseDown={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.preventDefault();
              handleClear();
            }}
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              // Keep onClick for keyboard accessibility (Enter key)
              e.preventDefault();
              handleClear();
            }}
            onKeyDown={(e) => {
              if (e.keyCode === ESC_KEY) {
                e.preventDefault();
                locationSelectorRef.current?.onCloseMenu();
                focusFilterButton();
              }
            }}
          >
            {contextTranslate('cmp-traveler-interests-clear-button', 'Clear')}
          </Button>
        </Col>
        <Col>
          <Button
            focusReferences={doneButtonRef}
            color="primary"
            {...doneButtonSettings}
            disabled={
              alwaysEnabledDoneButton ? false : selectedItems.length === 0 && !activateDoneButton
            }
            onMouseDown={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.preventDefault();
              handleSave();
            }}
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              // Keep onClick for keyboard accessibility (Enter key)
              e.preventDefault();
              handleSave();
            }}
            onKeyDown={(e) => {
              if ((!e.shiftKey && e.key === 'Tab' && !focusLock) || e.keyCode === ESC_KEY) {
                e.preventDefault();
                locationSelectorRef.current?.onCloseMenu();
                focusFilterButton();
              }
            }}
          >
            {contextTranslate('cmp-traveler-interests-done-button', 'Done')}
          </Button>
        </Col>
      </Row>
      <VisuallyHidden aria-live="polite">
        {count === 0
          ? contextTranslate('cmp-traveler-info-not-options-selected', 'No item selected')
          : ''}
      </VisuallyHidden>
    </Box>
  );

  //TODO: Duplicate function
  //src/components/controls/SecondaryFilters/TravelerInterest.jsx
  const selectItem = (itemMarked: ItemProps) => {
    if (itemMarked.value === ITEM_ANY_ELEMENTS) {
      if (selectedItems.length === options.length) {
        return [];
      } else {
        return options;
      }
    } else {
      if (selectedItems.length === 0) {
        return [itemMarked];
      } else {
        const index = selectedItems.findIndex((item) => item.value === itemMarked.value);
        if (index !== -1 && itemMarked.value !== ITEM_ANY_ELEMENTS) {
          // const optionsWithoutAllItems = selectedItems.filter(
          //   (e) => e.value !== ITEM_ANY_ELEMENTS
          // );

          // } else {
          let aux = [...selectedItems];
          aux.splice(index, 1);
          const index1 = aux.findIndex((item) => item.value === ITEM_ALL_ELEMENTS.value);
          if (index1 !== -1) {
            aux.splice(index1, 1);
          }
          return aux;
          // }
        } else {
          if (itemMarked.value === ITEM_ANY_ELEMENTS) {
            return options;
          } else {
            let tmp = [...selectedItems];
            tmp.push(itemMarked);
            if (tmp.length === options.length - 1) {
              return options;
            }
            return tmp;
          }
        }
      }
    }
  };

  const onKeyUp = (e: { keyCode: number }) => {
    // user keypress Esc inside a item
    if (e.keyCode === ESC_KEY) {
      locationSelectorRef.current?.onCloseMenu();
      focusFilterButton();
      if (setWasClear) {
        setWasClear(true);
      }
      // Cleaning items selected when user press Esc
      if (Boolean(selectedItemsState[travelInterestId])) {
        setSelectedItems(selectedItemsState[travelInterestId].selectedItems);
      } else {
        setSelectedItems([]);
      }
    }
  };
  const onKeyDown = (e: { keyCode: number; shiftKey: boolean }) => {
    if (e.keyCode === TAB_KEY && e.shiftKey) {
      locationSelectorRef.current?.onCloseMenu();
    }
    if (e.keyCode === ARROW_UP_KEY || e.keyCode === ARROW_DOWN_KEY) {
      locationSelectorRef.current?.onOpenMenu();
    }
  };

  const newProps = {
    customEvents: true,
    ...props,
    inputItem: {
      ...props.inputItem,
      labelSeparator: contextTranslate(props.labelKeySeparatorMultiSelection, ', ')
    },
    menuItem: {
      ...props.menuItem,
      className: props.navigation
        ? props.enableSecondarySearch
          ? 'fr-interest-search'
          : 'fr-interest-not-search'
        : ''
    }
  };

  return (
    <EmotionCache direction={direction}>
      <ThemeProvider theme={designTokens}>
        <LocationSelector
          enableSecondarySearch={enableSecondarySearch}
          focusLock={focusLock}
          useAsDialog={useAsDialog}
          dialogAriaLabel={dialogAriaLabel}
          labelKey="interest"
          selectedItems={selectedItems}
          multipleSelection
          multipleSelectionGroupLabel={false}
          options={options}
          dataTestId="locationSelector-interest"
          placeholder={contextTranslate(
            'cmp-traveler-interest-filter-placeholder',
            'Select Interests'
          )}
          inputFocusReferences={ref ?? locationSelectorInputRef}
          secondarySearchRef={secondarySearchFilterRef}
          itemRenderer={renderItem}
          isMobile={isMobile}
          openOnClick
          {...newProps}
          onClickInput={onClick}
          onChange={(item: $TSFixMe) => {
            if (item !== undefined) {
              // if (selectedItems.length === 0) {
              //   if (!props.isMobile) {
              //     setSelectedItems([item]);
              //     props.onChange([item]);
              //     return;
              //   } else {
              //     props.onChange([item]);
              //     return;
              //   }
              // }
              const newSelectedItems: [ItemProps] | any[] = selectItem(item);
              setSelectedItems(newSelectedItems);
              //INFO: clearing the secondary search filter after selecting an item in case it has value
              if (secondarySearchFilterRef?.current?.getValue() !== '') {
                secondarySearchFilterRef?.current?.clear();
                setItemSelectedByKeyboard(item);
              }
            } else {
              // props.onChange(selectedItems);
            }
          }}
          onKeyUp={onKeyUp}
          onKeyDown={onKeyDown}
          footerElement={!isMobile && renderFooter}
          ref={locationSelectorRef}
          useInput={false}
          notifyState={(e: any) => {
            setIsOpen(e);
          }}
          defaultIsOpen={navigation}
          toggleButtonLabels={labelSelectedItems}
          showToggleButtonLabels={showToggleButtonLabels}
          isDirty={isDirty}
          onBlur={() => {
            if (isDirty) {
              setWasClear(false);
            }
          }}
          slowCloseAction={true}
          isConfirmClearAction={activateDoneButton === false}
          copySelectedItems={copySelectedItems}
        />
        {props.navigation && (
          <MobileFooter align="end" sx={{ width: '100%', right: 0 }}>
            {renderFooter()}
          </MobileFooter>
        )}
      </ThemeProvider>
    </EmotionCache>
  );
}

TravelerInterest.componentName = 'TravelerInterest';

export default React.forwardRef(TravelerInterest);
